import React from 'react';
import { Typography, Paper, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TranslateIcon from '@mui/icons-material/Translate';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const AITalkerConfiguration = () => {
  const configSections = [
    {
      icon: <RecordVoiceOverIcon />,
      title: "Voice Settings",
      content: "Customize the voice of your AI Talker. Choose from a variety of voices, adjust speech rate, pitch, and volume to match your brand identity and target audience preferences. You can also create custom voice profiles for different use cases or languages."
    },
    {
      icon: <TranslateIcon />,
      title: "Language Configuration",
      content: "Set up your AI Talker to communicate in multiple languages. Define primary and secondary languages, configure language detection settings, and manage translation capabilities. This allows your AI Talker to seamlessly switch between languages based on user preferences or detected input language."
    },
    {
      icon: <PersonalVideoIcon />,
      title: "Personality and Behavior",
      content: "Shape the personality of your AI Talker to align with your brand voice. Adjust traits like friendliness, formality, and humor. Define conversation styles, create custom responses for specific scenarios, and set up appropriate fallback responses for situations outside the AI's knowledge base."
    },
    {
      icon: <StorageIcon />,
      title: "Knowledge Base",
      content: "Build and manage the AI Talker's knowledge base. Import FAQs, product information, company policies, and other relevant data. Set up learning algorithms to continuously improve the AI's knowledge from interactions. You can also define confidence thresholds for when the AI should escalate to human support."
    },
    {
      icon: <AccountTreeIcon />,
      title: "Conversation Flow",
      content: "Design conversation flows for different use cases. Set up decision trees, define user intents and entities, and create dynamic conversation paths. Configure how the AI handles interruptions, manages context switching, and maintains conversation coherence across multiple turns."
    }
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>AI Talker Configuration</Typography>
      <Typography paragraph>
        Configuring your AI Talker is a crucial step in ensuring it meets your specific business needs and provides 
        the best possible experience for your users. Our comprehensive configuration options allow you to 
        fine-tune every aspect of your AI Talker's behavior and capabilities.
      </Typography>
      {configSections.map((section, index) => (
        <Accordion key={index}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel${index}a-content`}
            id={`panel${index}a-header`}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              {section.icon}
              <span style={{ marginLeft: '10px' }}>{section.title}</span>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              {section.content}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
      <Typography variant="h6" style={{ marginTop: '20px' }}>Best Practices for Configuration:</Typography>
      <Typography paragraph>
        1. <strong>Start with a Clear Goal:</strong> Define what you want your AI Talker to achieve before starting the configuration process.
      </Typography>
      <Typography paragraph>
        2. <strong>Test Thoroughly:</strong> Use the AI Talker Playground to test different configurations and ensure the AI behaves as expected.
      </Typography>
      <Typography paragraph>
        3. <strong>Iterate and Refine:</strong> Regularly review your AI Talker's performance and user feedback to make necessary adjustments.
      </Typography>
      <Typography paragraph>
        4. <strong>Balance Personality and Professionalism:</strong> Ensure your AI Talker's personality aligns with your brand while maintaining a professional demeanor.
      </Typography>
      <Typography paragraph>
        5. <strong>Keep Your Knowledge Base Updated:</strong> Regularly update the AI's knowledge base to ensure it provides accurate and up-to-date information.
      </Typography>
      <Typography paragraph>
        Remember, configuring your AI Talker is an ongoing process. As you gather more data and insights from 
        user interactions, you'll be able to fine-tune your AI Talker to deliver even better results over time.
      </Typography>
      <Typography paragraph>
        If you need assistance with configuring your AI Talker, our support team is always available to help. 
        We also offer professional services for custom configuration and optimization to meet complex business requirements.
      </Typography>
    </Paper>
  );
};

export default AITalkerConfiguration;
